import BattleAction from './BattleAction.js';
import ActionType from './ActionType.js';
import { ANIMATION_SPEED, pinchBerryStat } from '../../constants.js';
import { HazardType } from '../../entities/EntryHazard.js';
import Status from '../../entities/Status.js';

export default class BattleStateMachineActionGenerator {
    constructor({ stateMachine, d }) {
        this.stateMachine = stateMachine;
        this.d = d;
    }

    finishReading(options = { debounce: 0, startAction: () => {}, endAction: () => {} }) {
        return new BattleAction({
            startAction: options.startAction,
            type: ActionType.FINISH_READING,
            description: 'Wait for user to press space',
            battleStateMachine: this.stateMachine,
            debounce: options.debounce,
            endAction: options.endAction,
        });
    }

    hideUIElement(element) {
        return new BattleAction({
            type: ActionType.ANIMATION,
            animationFunction: () => {
                element.visible = false; // Access the element property
                return true;
            },
        });
    }

    showUIElement(element) {
        return new BattleAction({
            type: ActionType.ANIMATION,
            animationFunction: () => {
                element.visible = true;
                return true;
            },
        });
    }

    fadeOutUIElement(element, duration = 60) {
        return new BattleAction({
            type: ActionType.ANIMATION,
            duration: duration,
            animationFunction: () => {
                element.sprite.fadeOut(duration);
                return true;
            },
        });
    }

    fadeInUIElement(element, duration = 60) {
        return new BattleAction({
            type: ActionType.ANIMATION,
            duration: duration,
            animationFunction: () => {
                element.sprite.fadeIn(duration);
                return true;
            },
        });
    }

    moveUIElement(element, xDistance, yDistance, speed = 2) {
        let startX, startY, targetX, targetY;
        const actualSpeed = speed * ANIMATION_SPEED;

        return new BattleAction({
            type: ActionType.ANIMATION,
            description: `Move UI element by (${xDistance}, ${yDistance})`,
            startAction: () => {
                startX = element.position.x;
                startY = element.position.y;
                targetX = startX + xDistance;
                targetY = startY + yDistance;
            },
            animationFunction: () => {
                // Calculate direction and move
                if (xDistance !== 0) {
                    const xDirection = xDistance > 0 ? 1 : -1;
                    element.position.x += xDirection * actualSpeed;
                    // Clamp to target if we've overshot
                    if (xDistance > 0 && element.position.x >= targetX) {
                        element.position.x = targetX;
                    } else if (xDistance < 0 && element.position.x <= targetX) {
                        element.position.x = targetX;
                    }
                }

                if (yDistance !== 0) {
                    const yDirection = yDistance > 0 ? 1 : -1;
                    element.position.y += yDirection * actualSpeed;
                    // Clamp to target if we've overshot
                    if (yDistance > 0 && element.position.y >= targetY) {
                        element.position.y = targetY;
                    } else if (yDistance < 0 && element.position.y <= targetY) {
                        element.position.y = targetY;
                    }
                }

                // Return true when we've reached or passed both targets
                const xReached =
                    xDistance === 0 ||
                    (xDistance > 0 && element.position.x >= targetX) ||
                    (xDistance < 0 && element.position.x <= targetX);
                const yReached =
                    yDistance === 0 ||
                    (yDistance > 0 && element.position.y >= targetY) ||
                    (yDistance < 0 && element.position.y <= targetY);
                return xReached && yReached;
            },
        });
    }

    clearText() {
        return new BattleAction({
            type: ActionType.CODE,
            action: () => {
                this.stateMachine.battleText = '';
                return true;
            },
        });
    }

    readText(
        text,
        options = {
            useCursor: true,
            delay: 0,
            showContinueArrow: true,
        }
    ) {
        this.stateMachine.battleGUI.text.visible = true;
        // Set continue arrow visibility option
        if (this.stateMachine.battleGUI && this.stateMachine.battleGUI.text) {
            this.stateMachine.battleGUI.text.showContinueArrow = options.showContinueArrow;
        }
        return new BattleAction({
            type: ActionType.TEXT,
            text: text,
            useCursor: options.useCursor,
            description: text,
            delay: options.delay,
        });
    }

    instantText(text, options = {}) {
        return this.readText(text, { useCursor: false, ...options });
    }

    script(script) {
        return new BattleAction({
            type: ActionType.CODE,
            action: () => {
                script();
                return true;
            },
        });
    }

    delay(delayAmount) {
        return new BattleAction({
            type: ActionType.DELAY,
            delay: delayAmount,
            description: `${delayAmount} frame delay...`,
        });
    }

    playBattleCry(pokemon) {
        return new BattleAction({
            type: ActionType.AUDIO,
            description: 'Foe performs battle cry',
            audioFilePath: `audio/battle_cries/cries/${pokemon.id.toString().padStart(3, '0')}.mp3`,
        });
    }

    playAudio(path) {
        return new BattleAction({
            type: ActionType.AUDIO,
            description: `Playing ${path}`,
            audioFilePath: path,
        });
    }

    resetAssetDarkness(options = { asset: null, duration: 30 }) {
        if (options.asset === null) {
            console.warn('No options proivided.');
            return this.blankAction();
        }

        return new BattleAction({
            type: ActionType.CODE,
            action: () => {
                options.asset.resetDarkness(options.duration);
                return true;
            },
        });
    }

    blankAction() {
        return new BattleAction({
            type: ActionType.CODE,
            action: () => {
                return true;
            },
        });
    }

    clearMenus() {
        return this.script(() => {
            this.d.pokemonMenu.visible = false;
            this.d.bagMenu.visible = false;
            this.d.overworldTextBox.visible = false;
        });
    }

    animatePlayerTrainerThrowingPokeball(pokemon) {
        return new BattleAction({
            type: ActionType.ANIMATION,
            description: 'Animate player throwing pokeball while moving to the left out of the screen',
            startAction: () => {
                pokemon.pokeball.battleSprites.back.position = {
                    x: this.d.trainerPokeball.position.x,
                    y: this.d.trainerPokeball.position.y,
                };
                this.d.trainerPokeball = pokemon.pokeball.battleSprites.back;
                this.d.trainerPokeball.position.y = this.d.player.position.y;

                this.d.thrownPokeball = pokemon.pokeball.battleSprites.thrown;
                this.d.thrownPokeball.width = 16;
                this.d.thrownPokeball.origin = {
                    x: 0.5,
                    y: 0.625,
                };
                this.d.thrownPokeball.frames.val = 0;
                this.d.thrownPokeball.moving = false;
                this.d.thrownPokeball.position.x = this.d.player.position.x + 8;
                this.d.thrownPokeball.position.y = this.d.player.position.y + 8;
                this.d.thrownPokeball.visible = false;
                this.d.thrownPokeball.yVelocity = -1 * ANIMATION_SPEED;
                this.d.thrownPokeball.gravity = 0.03 * ANIMATION_SPEED ** 2;
            },
            animationFunction: () => {
                this.d.thrownPokeball.width = 16;
                this.d.player.moving = true;
                this.d.player.hold = 16 / ANIMATION_SPEED;
                this.d.player.frames.stopAtEnd = true;
                this.d.player.position.x -= 1 * ANIMATION_SPEED;
                this.d.trainerPokeball.moving = true;
                this.d.trainerPokeball.hold = 16 / ANIMATION_SPEED;
                this.d.trainerPokeball.frames.stopAtEnd = true;
                this.d.thrownPokeball.visible = false;
                this.d.thrownPokeball.origin = {
                    x: 0.5,
                    y: 0.625,
                };

                if (this.d.thrownPokeball.yVelocity < 0 && this.d.thrownPokeball.yVelocity > -0.35) {
                    this.d.thrownPokeball.gravity = 0.015 * ANIMATION_SPEED ** 2;
                }

                if (this.d.thrownPokeball.yVelocity > 0) {
                    if (this.d.thrownPokeball.yVelocity > 1 && this.d.thrownPokeball.yVelocity < 2) {
                        this.d.thrownPokeball.yVelocity += 2;
                        this.d.thrownPokeball.gravity = 0.03 * ANIMATION_SPEED ** 2;
                    }
                }
                if (
                    this.d.player.frames.val === this.d.player.frames.max - 1 &&
                    this.d.player.position.x < -20
                ) {
                    this.d.thrownPokeball.visible = true;
                    this.d.thrownPokeball.frames.val = 0;
                    this.d.thrownPokeball.constantRotationSpeed = -30;
                    this.d.thrownPokeball.position.x += 0.15 * ANIMATION_SPEED;
                    this.d.thrownPokeball.yVelocity += this.d.thrownPokeball.gravity;
                    this.d.thrownPokeball.position.y += this.d.thrownPokeball.yVelocity;
                }
                return this.d.player.position.x <= -64 && this.d.thrownPokeball.position.y > 112;
            },
            endAction: () => {
                this.d.thrownPokeball.visible = false;
                this.d.thrownPokeball.frames.val = 0;
                this.d.thrownPokeball.constantRotationSpeed = 0;
                this.d.thrownPokeball.resetRotation();
            },
            battleStateMachine: this.stateMachine,
        });
    }

    sendOutPlayerPokemon(pokemon) {
        return [
            this.script(() => {
                pokemon.loadBattleCry();
            }),
            new BattleAction({
                type: ActionType.ANIMATION,
                description: 'Animate player throwing ball from the left side of the screen',
                startAction: () => {
                    this.d.thrownPokeball = pokemon.pokeball.battleSprites.thrown;
                    this.d.thrownPokeball.width = 16;
                    this.d.thrownPokeball.origin = {
                        x: 0.5,
                        y: 0.625,
                    };
                    this.d.thrownPokeball.frames.val = 0;
                    this.d.thrownPokeball.moving = false;
                    this.d.thrownPokeball.position.x = -12;
                    this.d.thrownPokeball.position.y = this.d.player.position.y + 8;
                    this.d.thrownPokeball.visible = true;
                    this.d.thrownPokeball.yVelocity = -1.3 * ANIMATION_SPEED;
                    this.d.thrownPokeball.gravity = 0.03 * ANIMATION_SPEED ** 2;
                    this.d.thrownPokeball.constantRotationSpeed = -30;
                },
                animationFunction: () => {
                    this.d.thrownPokeball.width = 16;
                    this.d.playerPokemon.visible = false;
                    this.d.thrownPokeball.visible = true;
                    this.d.thrownPokeball.moving = false;
                    this.d.thrownPokeball.frames.val = 0;
                    this.d.thrownPokeball.position.x += 0.6 * ANIMATION_SPEED;
                    this.d.thrownPokeball.yVelocity += this.d.thrownPokeball.gravity;
                    this.d.thrownPokeball.position.y += this.d.thrownPokeball.yVelocity;
                    return this.d.thrownPokeball.position.y > 112;
                },
                endAction: () => {
                    this.d.thrownPokeball.constantRotationSpeed = 0;
                    this.d.thrownPokeball.resetRotation();
                },
            }),
            this.playAudio('audio/sfx/spawn_from_pokeball.mp3'),
            new BattleAction({
                type: ActionType.ANIMATION,
                description: 'Animate player pokemon spawning from pokeball',
                startAction: () => {
                    this.d.playerPokemon.scale = 0;
                    this.d.playerPokemon.visible = true;
                    this.d.playerPokemon.verticalCrop = 0;
                    this.d.playerPokemon.verticalOffset = 0;
                    this.d.whiteFlash.flash();
                },
                animationFunction: () => {
                    if (this.d.playerPokemon.scale + 0.02 * ANIMATION_SPEED < 1) {
                        this.d.playerPokemon.scale += 0.02 * ANIMATION_SPEED;
                    } else {
                        this.d.playerPokemon.scale = 1;
                    }
                    this.d.playerPokemon.position.x = 32 + 32 * (1 - this.d.playerPokemon.scale);
                    this.d.playerPokemon.position.y =
                        48 + 32 * (1 - this.d.playerPokemon.scale) + 32 * (1 - this.d.playerPokemon.scale);
                    return this.d.playerPokemon.scale === 1;
                },
            }),
            this.playBattleCry(pokemon),
            new BattleAction({
                type: ActionType.ANIMATION,
                description: 'Animate player information box from the right side of the screen',
                startAction: () => {
                    this.d.playerInfo.position.x = 240;
                    this.d.playerInfo.visible = true;
                },
                animationFunction: () => {
                    this.d.playerInfo.position.x -= 2 * ANIMATION_SPEED;
                    return this.d.playerInfo.position.x <= 240 - 96 - 16;
                },
            }),
            this.delay(100),
            // Process entry hazards (player Pokemon is affected by foe's entry hazards)
            ...this.processEntryHazards(pokemon, this.stateMachine.entryHazardsPlayer),
        ];
    }

    sendOutFoePokemon(pokemon) {
        const sendOutFoeActions = [
            this.script(() => {
                pokemon.loadBattleCry();
            }),
            new BattleAction({
                type: ActionType.ANIMATION,
                description:
                    'Animate foe trainer throwing pokeball while moving to the left out of the screen',
                startAction: () => {
                    this.d.thrownPokeball.position.x = 200;
                    this.d.thrownPokeball.position.y = this.d.foeTrainer.position.y + 8;
                    this.d.thrownPokeball.visible = false;
                    this.d.thrownPokeball.yVelocity = -1 * ANIMATION_SPEED;
                    this.d.thrownPokeball.gravity = 0.03 * ANIMATION_SPEED ** 2;
                    this.d.thrownPokeball.setImage(this.d.images.thrownPokeballImage);
                    this.d.thrownPokeball.frames.max = 8;
                    this.d.thrownPokeball.visible = false;
                    this.d.foePokemon.visible = false;
                },
                animationFunction: () => {
                    this.d.foeTrainer.position.x += ANIMATION_SPEED;
                    if (this.d.foeTrainer.position.x > 160) {
                        this.d.thrownPokeball.visible = true;
                        this.d.thrownPokeball.moving = true;
                        this.d.thrownPokeball.hold = 4;
                        this.d.thrownPokeball.position.x -= 0.2 * ANIMATION_SPEED;
                        this.d.thrownPokeball.yVelocity += this.d.thrownPokeball.gravity;
                        this.d.thrownPokeball.position.y += this.d.thrownPokeball.yVelocity;
                    }
                    return this.d.foeTrainer.position.x > 240 && this.d.thrownPokeball.position.y > 48;
                },
                endAction: () => {
                    this.d.thrownPokeball.visible = false;
                    this.d.thrownPokeball.frames.val = 0;
                },
                battleStateMachine: this,
            }),
            this.playAudio('audio/sfx/spawn_from_pokeball.mp3'),
            this.script(() => {
                this.d.foePokemon.scale = 0;
            }),
            this.showUIElement(this.d.foePokemon),
            new BattleAction({
                type: ActionType.ANIMATION,
                description: "Animate foe trainer's pokemon spawning from pokeball",
                startAction: () => {
                    this.d.foePokemon.scale = 0;
                    this.d.foePokemon.position.x = 144;
                    this.d.foePokemon.position.y = 80;
                    this.d.foePokemon.verticalCrop = 0;
                    this.d.foePokemon.verticalOffset = 0;
                    this.d.whiteFlash.flash();
                },
                animationFunction: () => {
                    if (this.d.foePokemon.scale + 0.02 * ANIMATION_SPEED < 1) {
                        this.d.foePokemon.scale += 0.02 * ANIMATION_SPEED;
                    } else {
                        this.d.foePokemon.scale = 1;
                    }
                    this.d.foePokemon.position.x = 144 + 32 * (1 - this.d.foePokemon.scale);
                    this.d.foePokemon.position.y =
                        16 + 32 * (1 - this.d.foePokemon.scale) + 16 * (1 - this.d.foePokemon.scale);
                    return this.d.foePokemon.scale === 1;
                },
                battleStateMachine: this,
            }),
            this.playAudio(
                `audio/battle_cries/cries/${(this.d.foePokemon.spriteIndex + 1)
                    .toString()
                    .padStart(3, '0')}.mp3`
            ),
            new BattleAction({
                type: ActionType.ANIMATION,
                description: 'Animate foe information box into the screen from the left',
                startAction: () => {
                    this.d.foeInfo.position.x = -120;
                    this.d.foeInfo.visible = true;
                },
                animationFunction: () => {
                    this.d.foeInfo.position.x += 2 * ANIMATION_SPEED;
                    return this.d.foeInfo.position.x === 0;
                },
                battleStateMachine: this,
            }),

            // Process entry hazards (foe Pokemon is affected by player's entry hazards)
            ...this.processEntryHazards(pokemon, this.stateMachine.entryHazardsFoe),
        ];

        return sendOutFoeActions;
    }

    updateHP(pokemon, hpDelta) {
        return new BattleAction({
            type: ActionType.ANIMATION,
            description: 'Animate health bar update',
            animationFunction: () => {
                if (hpDelta < 0) {
                    // Decreasing HP
                    pokemon.stats.hp -= 0.5;
                    hpDelta += 0.5;
                } else if (hpDelta > 0) {
                    // Increasing HP
                    if (pokemon.stats.hp < pokemon.stats.maxHp) {
                        pokemon.stats.hp += 0.5;
                    }
                    hpDelta -= 0.5;
                }
                return hpDelta === 0 || pokemon.stats.hp === 0 || pokemon.stats.hp === pokemon.stats.maxHp;
            },
        });
    }

    /**
     * Process entry hazards for a Pokemon entering the field.
     * @param {Pokemon} pokemon - The Pokemon entering the field
     * @param {Object} entryHazards - The entry hazards object (entryHazardsFoe or entryHazardsPlayer)
     * @returns {Array} Array of BattleActions for entry hazard effects
     */
    processEntryHazards(pokemon, entryHazards) {
        const actions = [];

        // If no entry hazards exist, return empty array
        if (!entryHazards || Object.keys(entryHazards).length === 0) {
            return actions;
        }

        // Process each entry hazard
        Object.keys(entryHazards).forEach((hazardKey) => {
            const hazardType = hazardKey;
            const layers = entryHazards[hazardType];

            if (!layers || layers <= 0) {
                return; // Skip if no layers
            }

            // Calculate and apply damage
            const damage = pokemon.calculateEntryHazardDamage(hazardType, layers);
            if (damage > 0) {
                // Get hazard name for display
                const hazardNames = {
                    [HazardType.SPIKES]: 'Spikes',
                    [HazardType.STEALTH_ROCK]: 'Stealth Rock',
                    [HazardType.TOXIC_SPIKES]: 'Toxic Spikes',
                    [HazardType.STICKY_WEB]: 'Sticky Web',
                };
                const hazardName = hazardNames[hazardType] || hazardType;

                actions.push(this.readText(`${pokemon.name} is hurt by ${hazardName}!`));
                actions.push(this.delay(50));
                actions.push(this.updateHP(pokemon, -damage));
            }

            // Apply status effects
            const statusEffect = pokemon.getEntryHazardStatusEffect(hazardType, layers);
            if (statusEffect) {
                const statusNames = {
                    poison: 'poisoned',
                    bad_poison: 'badly poisoned',
                };
                actions.push(
                    this.script(() => {
                        const newStatus = new Status({
                            id: statusEffect,
                            startTurn: 0,
                            duration: Status.getDuration(statusEffect),
                            pokemon: pokemon,
                        });
                        pokemon.status1 = newStatus;
                    })
                );
                const statusName = statusNames[statusEffect] || statusEffect;
                actions.push(this.readText(`${pokemon.name} was ${statusName}!`));
                actions.push(this.delay(50));
            }

            // Apply stat modifications
            const statMod = pokemon.getEntryHazardStatModification(hazardType);
            if (statMod) {
                // Create stat modifications object in simple format { stat: stages }
                const statModifications = { [statMod.stat]: statMod.stages };

                // Apply the stat change first (before animation)
                actions.push(
                    this.script(() => {
                        pokemon.battleStages[statMod.stat] = Math.max(
                            -6,
                            Math.min(6, pokemon.battleStages[statMod.stat] + statMod.stages)
                        );
                    })
                );

                // Animate the stat change
                actions.push(...this.animateStatChange(pokemon, statModifications));

                // Show text notification
                const statNames = {
                    speed: 'Speed',
                    attack: 'Attack',
                    defense: 'Defense',
                    specialAttack: 'Special Attack',
                    specialDefense: 'Special Defense',
                };
                const statName = statNames[statMod.stat] || statMod.stat;
                const direction = statMod.stages > 0 ? 'rose' : 'fell';
                actions.push(this.readText(`${pokemon.name}'s ${statName} ${direction}!`));
                actions.push(this.delay(50));
            }
        });

        return actions;
    }

    /**
     * Apply Effect Spore ability: 10% chance to apply sleep, paralysis, or poison when hit by or hitting with a contact move.
     * @param {Pokemon} attacker - The attacking Pokemon
     * @param {Pokemon} defender - The defending Pokemon
     * @param {Move} move - The move being used
     * @returns {Array} Array of BattleActions for the effect spore ability, empty if it doesn't trigger
     */
    applyEffectSporeEffect(attacker, defender, move) {
        const actions = [];

        // Only trigger on contact moves
        if (!move.makesContact()) {
            return actions;
        }

        // Check if attacker has effect_spore ability (attacker hits defender with contact move)
        if (attacker.ability?.identifier === 'effect_spore') {
            if (!defender.status1 && Math.random() < 0.1) {
                const possibleStatuses = ['sleep', 'paralysis', 'poison'];
                const selectedStatus = possibleStatuses[Math.floor(Math.random() * possibleStatuses.length)];
                if (selectedStatus === 'sleep' && defender.ability?.identifier === 'insomnia') {
                    actions.push(
                        this.readText(
                            `${defender.name}'s Insomnia prevented it from falling asleep!`,
                            { delay: 50 }
                        )
                    );
                } else {
                    const newStatus = new Status({
                        id: selectedStatus,
                        startTurn: 0,
                        duration: Status.getDuration(selectedStatus),
                        pokemon: defender,
                    });
                    const statusTexts = {
                        sleep: `${defender.name} fell asleep due to ${attacker.name}'s Effect Spore!`,
                        paralysis: `${defender.name} was paralyzed due to ${attacker.name}'s Effect Spore!`,
                        poison: `${defender.name} was poisoned due to ${attacker.name}'s Effect Spore!`,
                    };
                    actions.push(
                        ...this.applyStatusThenHeldBerryCureActions(
                            defender,
                            newStatus,
                            statusTexts[selectedStatus],
                            selectedStatus
                        )
                    );
                }
            }
        }

        // Check if defender has effect_spore ability (defender is hit by contact move)
        if (defender.ability?.identifier === 'effect_spore') {
            if (!attacker.status1 && Math.random() < 0.1) {
                const possibleStatuses = ['sleep', 'paralysis', 'poison'];
                const selectedStatus = possibleStatuses[Math.floor(Math.random() * possibleStatuses.length)];
                if (selectedStatus === 'sleep' && attacker.ability?.identifier === 'insomnia') {
                    actions.push(
                        this.readText(
                            `${attacker.name}'s Insomnia prevented it from falling asleep!`,
                            { delay: 50 }
                        )
                    );
                } else {
                    const newStatus = new Status({
                        id: selectedStatus,
                        startTurn: 0,
                        duration: Status.getDuration(selectedStatus),
                        pokemon: attacker,
                    });
                    const statusTexts = {
                        sleep: `${attacker.name} fell asleep due to ${defender.name}'s Effect Spore!`,
                        paralysis: `${attacker.name} was paralyzed due to ${defender.name}'s Effect Spore!`,
                        poison: `${attacker.name} was poisoned due to ${defender.name}'s Effect Spore!`,
                    };
                    actions.push(
                        ...this.applyStatusThenHeldBerryCureActions(
                            attacker,
                            newStatus,
                            statusTexts[selectedStatus],
                            selectedStatus
                        )
                    );
                }
            }
        }

        return actions;
    }

    /**
     * Apply Poison Point ability: 30% chance to poison when hit by or hitting with a contact move.
     * Does not affect Poison or Steel type Pokemon.
     * @param {Pokemon} attacker - The attacking Pokemon
     * @param {Pokemon} defender - The defending Pokemon
     * @param {Move} move - The move being used
     * @returns {Array} Array of BattleActions for the poison point ability, empty if it doesn't trigger
     */
    applyPoisonPointEffect(attacker, defender, move) {
        const actions = [];

        // Only trigger on contact moves
        if (!move.makesContact()) {
            return actions;
        }

        // Helper function to check if a Pokemon is Poison or Steel type
        const isPoisonOrSteelType = (pokemon) => {
            return (
                pokemon.type1 === 'poison' ||
                pokemon.type1 === 'steel' ||
                pokemon.type2 === 'poison' ||
                pokemon.type2 === 'steel'
            );
        };

        // Check if attacker has poison_point ability (attacker hits defender with contact move)
        if (attacker.ability?.identifier === 'poison_point') {
            if (!isPoisonOrSteelType(defender) && !defender.status1 && Math.random() < 1.3) {
                const newStatus = new Status({
                    id: 'poison',
                    startTurn: 0,
                    duration: Status.getDuration('poison'),
                    pokemon: defender,
                });
                actions.push(
                    ...this.applyStatusThenHeldBerryCureActions(
                        defender,
                        newStatus,
                        `${defender.name} was poisoned due to ${attacker.name}'s Poison Point!`,
                        'poison'
                    )
                );
            }
        }

        // Check if defender has poison_point ability (defender is hit by contact move)
        if (defender.ability?.identifier === 'poison_point') {
            if (!isPoisonOrSteelType(attacker) && !attacker.status1 && Math.random() < 0.3) {
                const newStatus = new Status({
                    id: 'poison',
                    startTurn: 0,
                    duration: Status.getDuration('poison'),
                    pokemon: attacker,
                });
                actions.push(
                    ...this.applyStatusThenHeldBerryCureActions(
                        attacker,
                        newStatus,
                        `${attacker.name} was poisoned due to ${defender.name}'s Poison Point!`,
                        'poison'
                    )
                );
            }
        }

        return actions;
    }

    /**
     * Animate stat change for a Pokemon.
     * @param {Pokemon} pokemon - The Pokemon whose stat is changing
     * @param {Object} statModifications - Object where keys are stat names and values are stage changes (e.g., { speed: -2 } or { attack: 1, speed: 1 })
     * @returns {Array} Array of BattleActions for the stat change animation
     */
    animateStatChange(pokemon, statModifications) {
        const actions = [];

        // Get the list of modified stats
        const modifiedStats = Object.keys(statModifications);
        if (modifiedStats.length === 0) {
            return actions; // No stat changes, no animation
        }

        // Determine if it's a single stat or multiple stats (for animation selection)
        const modifiedStat = modifiedStats.length > 1 ? 'mix' : modifiedStats[0];
        const statChangeAnimation = pokemon.statChangeAnimations[modifiedStat];

        // Determine if it's an increase or decrease (check the first stat's value)
        // If all stats have the same direction, use that; otherwise default to increase
        const firstStatValue = statModifications[modifiedStats[0]];
        const isIncrease = firstStatValue > 0;
        const audioFile = isIncrease ? 'audio/sfx/stat_increase.mp3' : 'audio/sfx/stat_decrease.mp3';

        // Standardized animation parameters
        const duration = 60;
        const fadeInDuration = 15;
        const fadeOutDuration = 15;
        const fadeOutStart = 45;
        const opacity = 0.75;

        // Use closure to share duration between startAction and animationFunction
        let animationDuration = duration;

        actions.push(this.playAudio(audioFile));
        actions.push(
            new BattleAction({
                pokemon: pokemon,
                type: ActionType.ANIMATION,
                description: 'Animate stat increase or decrease',
                duration: duration,
                startAction: () => {
                    animationDuration = duration;
                    statChangeAnimation.fadeIn(fadeInDuration, opacity);
                    statChangeAnimation.reverseAnimation = isIncrease;
                },
                animationFunction: () => {
                    if (animationDuration === duration - fadeOutStart) {
                        statChangeAnimation.fadeOut(fadeOutDuration);
                    }
                    animationDuration -= 1;
                    return animationDuration <= 0;
                },
            })
        );

        return actions;
    }

    /** Rotate sprite toward targetDegrees by angularSpeed per frame. Completes when target is reached or passed. */
    rotate(sprite, targetDegrees, angularSpeed = 1) {
        return new BattleAction({
            type: ActionType.ANIMATION,
            description: 'Rotate sprite',
            animationFunction: () => {
                sprite.rotate(angularSpeed);
                const reached =
                    (angularSpeed > 0 && sprite.rotation >= targetDegrees) ||
                    (angularSpeed < 0 && sprite.rotation <= targetDegrees);
                if (reached) {
                    sprite.rotation = targetDegrees;
                }
                return reached;
            },
        });
    }

    /** One-frame action: set sprite rotation to 0 and clear position modifiers (e.g. after tackle). */
    resetSpriteRotation(sprite) {
        return new BattleAction({
            type: ActionType.ANIMATION,
            description: 'Reset sprite rotation and modifiers',
            animationFunction: () => {
                sprite.rotation = 0;
                sprite.xModifier = 0;
                sprite.yModifier = 0;
                return true;
            },
        });
    }

    /**
     * Pushes berry consumption wiggle + text + updateHP actions for a pokemon whose held berry
     * has triggered (HP below threshold). Caller must have already cleared defender.heldItem if desired.
     */
    pushHeldBerryConsumptionActions(pokemon, berryName, healAmount) {
        const actions = this.stateMachine.actions;
        const sprite = pokemon.currentSprite;
        const wiggleDeg = 5;
        const degPerFrame = 0.5;

        if (pokemon.isBackFacing()) {
            actions.push(
                this.resetSpriteRotation(sprite),
                this.script(() => {
                    sprite.origin = { x: 0, y: 1 };
                }),
                this.rotate(sprite, wiggleDeg, degPerFrame),
                this.rotate(sprite, 0, -degPerFrame),
                this.rotate(sprite, wiggleDeg, degPerFrame),
                this.rotate(sprite, 0, -degPerFrame),
                this.resetSpriteRotation(sprite),
                this.script(() => {
                    sprite.origin = { x: 0.5, y: 0.5 };
                })
            );
        } else {
            actions.push(
                this.resetSpriteRotation(sprite),
                this.script(() => {
                    sprite.origin = { x: 1, y: 1 };
                }),
                this.rotate(sprite, -wiggleDeg, -degPerFrame),
                this.rotate(sprite, 0, degPerFrame),
                this.rotate(sprite, -wiggleDeg, -degPerFrame),
                this.rotate(sprite, 0, degPerFrame),
                this.resetSpriteRotation(sprite),
                this.script(() => {
                    sprite.origin = { x: 0.5, y: 0.5 };
                })
            );
        }
        actions.push(
            this.readText(`${pokemon.name}'s ${berryName} restored its HP!`, { delay: 50 }),
            this.updateHP(pokemon, healAmount)
        );
    }

    /**
     * Pushes berry consumption wiggle + stat boost (+1 stage) + text. Used for pinch berries when held at 25% HP.
     */
    pushHeldBerryStatBoostActions(pokemon, berryName, stat) {
        const actions = this.stateMachine.actions;
        const sprite = pokemon.currentSprite;
        const wiggleDeg = 5;
        const degPerFrame = 0.5;

        if (pokemon.isBackFacing()) {
            actions.push(
                this.resetSpriteRotation(sprite),
                this.script(() => {
                    sprite.origin = { x: 0, y: 1 };
                }),
                this.rotate(sprite, wiggleDeg, degPerFrame),
                this.rotate(sprite, 0, -degPerFrame),
                this.rotate(sprite, wiggleDeg, degPerFrame),
                this.rotate(sprite, 0, -degPerFrame),
                this.resetSpriteRotation(sprite),
                this.script(() => {
                    sprite.origin = { x: 0.5, y: 0.5 };
                })
            );
        } else {
            actions.push(
                this.resetSpriteRotation(sprite),
                this.script(() => {
                    sprite.origin = { x: 1, y: 1 };
                }),
                this.rotate(sprite, -wiggleDeg, -degPerFrame),
                this.rotate(sprite, 0, degPerFrame),
                this.rotate(sprite, -wiggleDeg, -degPerFrame),
                this.rotate(sprite, 0, degPerFrame),
                this.resetSpriteRotation(sprite),
                this.script(() => {
                    sprite.origin = { x: 0.5, y: 0.5 };
                })
            );
        }
        actions.push(
            this.script(() => {
                pokemon.battleStages[stat] = Math.min(6, pokemon.battleStages[stat] + 1);
            }),
            ...this.animateStatChange(pokemon, { [stat]: 1 })
        );
        const statNames = {
            attack: 'Attack',
            defense: 'Defense',
            specialAttack: 'Special Attack',
            specialDefense: 'Special Defense',
            speed: 'Speed',
        };
        const statName = statNames[stat] || stat;
        actions.push(
            this.readText(`${pokemon.name}'s ${berryName} raised its ${statName}!`, { delay: 50 })
        );
    }

    /**
     * Pushes berry consumption wiggle + status-cure message (no HP). Use when a held berry
     * cures or prevents a status on inflict.
     */
    pushHeldBerryStatusCureActions(pokemon, berryName, statusId) {
        const actions = this.stateMachine.actions;
        const sprite = pokemon.currentSprite;
        const wiggleDeg = 5;
        const degPerFrame = 0.5;

        if (pokemon.isBackFacing()) {
            actions.push(
                this.resetSpriteRotation(sprite),
                this.script(() => {
                    sprite.origin = { x: 0, y: 1 };
                }),
                this.rotate(sprite, wiggleDeg, degPerFrame),
                this.rotate(sprite, 0, -degPerFrame),
                this.rotate(sprite, wiggleDeg, degPerFrame),
                this.rotate(sprite, 0, -degPerFrame),
                this.resetSpriteRotation(sprite),
                this.script(() => {
                    sprite.origin = { x: 0.5, y: 0.5 };
                })
            );
        } else {
            actions.push(
                this.resetSpriteRotation(sprite),
                this.script(() => {
                    sprite.origin = { x: 1, y: 1 };
                }),
                this.rotate(sprite, -wiggleDeg, -degPerFrame),
                this.rotate(sprite, 0, degPerFrame),
                this.rotate(sprite, -wiggleDeg, -degPerFrame),
                this.rotate(sprite, 0, degPerFrame),
                this.resetSpriteRotation(sprite),
                this.script(() => {
                    sprite.origin = { x: 0.5, y: 0.5 };
                })
            );
        }
        const statusNames = {
            poison: 'poison',
            paralysis: 'paralysis',
            burn: 'burn',
            freeze: 'freeze',
            sleep: 'sleep',
            confusion: 'confusion',
        };
        const name = statusNames[statusId] || statusId;
        actions.push(
            this.readText(`${pokemon.name}'s ${berryName} cured its ${name}!`, { delay: 50 })
        );
    }

    /**
     * Returns a script action: when confusion would be applied, first set confusion and show
     * "X became confused!", then if held berry cures it, wiggle + "cured its confusion!" and clear.
     * Call from applyVolatileStatusEffect when receiver status is confusion.
     */
    heldBerryConfusionCureOrApplyAction(defender, statusTurns, statusChance) {
        const ag = this;
        return this.script(() => {
            if (defender.volatileStatuses.confusion > 0) {
                ag.stateMachine.actions.push(
                    ag.readText(`${defender.name} is already confused!`, { delay: 50 })
                );
                return;
            }
            if (Math.random() >= statusChance) return;
            defender.volatileStatuses.confusion = statusTurns;
            ag.stateMachine.actions.push(
                ag.readText(`${defender.name} became confused!`, { delay: 50 }),
                ag.heldBerryStatusCureAfterInflictConfusionAction(defender)
            );
        });
    }

    /**
     * Runs after "X became confused!" was shown: if held berry cures confusion, consume berry,
     * push wiggle + cure message, and clear volatileStatuses.confusion.
     */
    heldBerryStatusCureAfterInflictConfusionAction(pokemon) {
        const ag = this;
        return this.script(() => {
            const berry = pokemon?.heldItem;
            if (
                pokemon?.stats.hp > 0 &&
                berry?.healStatus?.length > 0 &&
                berry.healStatus.includes('confusion')
            ) {
                const berryName = berry.name;
                pokemon.heldItem = null;
                ag.pushHeldBerryStatusCureActions(pokemon, berryName, 'confusion');
                pokemon.volatileStatuses.confusion = 0;
            }
        });
    }

    /**
     * Returns a script action that runs after status was applied and announced: if the pokemon's
     * held berry cures this status, consume berry, push wiggle + "cured its X!", and clear status1.
     * Call after: apply status, then readText(affectedText), then this action.
     */
    heldBerryStatusCureAfterInflictAction(pokemon, statusId) {
        const ag = this;
        return this.script(() => {
            const berry = pokemon?.heldItem;
            if (
                pokemon?.stats.hp > 0 &&
                berry?.healStatus?.length > 0 &&
                berry.healStatus.includes(statusId)
            ) {
                const berryName = berry.name;
                pokemon.heldItem = null;
                ag.pushHeldBerryStatusCureActions(pokemon, berryName, statusId);
                pokemon.status1 = null;
            }
        });
    }

    /**
     * Returns actions for primary status application in the desired order:
     * 1. Apply status and show "X was poisoned!" (or other affectedText)
     * 2. If held berry cures it: wiggle + "X's Berry cured its Y!" and clear status
     */
    applyStatusThenHeldBerryCureActions(defender, newStatus, affectedText, statusId) {
        const actions = [];
        actions.push(
            this.script(() => {
                defender.status1 = newStatus;
            }),
            this.readText(affectedText, { delay: 50 }),
            this.heldBerryStatusCureAfterInflictAction(defender, statusId)
        );
        return actions;
    }

    /**
     * Returns a script action that checks context.defender for held berry trigger (HP below threshold)
     * and, if so, consumes the berry and pushes wiggle + text + updateHP. Use after dealDamage.
     */
    heldBerryConsumptionAfterDamageAction() {
        const ag = this;
        return this.script(() => {
            const bsm = ag.stateMachine;
            const defender = bsm.context.defender;
            const berry = defender?.heldItem;
            if (!defender || defender.stats.hp <= 0 || !berry || (berry.berryConsumeHpPercent === null || berry.berryConsumeHpPercent === undefined)) {
                return;
            }
            const hpPercent = (defender.stats.hp / defender.stats.maxHp) * 100;
            if (hpPercent >= berry.berryConsumeHpPercent) return;

            const berryName = berry.name;
            defender.heldItem = null;

            if (berry.healAmount !== null && berry.healAmount !== undefined && berry.healAmount > 0) {
                ag.pushHeldBerryConsumptionActions(defender, berryName, berry.healAmount);
            } else if (berry.id && pinchBerryStat[berry.id]) {
                ag.pushHeldBerryStatBoostActions(defender, berryName, pinchBerryStat[berry.id]);
            }
        });
    }

    /**
     * Returns a script action that checks the given pokemon for held berry trigger (HP below threshold)
     * and, if so, consumes the berry and pushes wiggle + HP restore or stat boost. Use after residual damage (e.g. poison).
     */
    heldBerryConsumptionIfTriggeredAction(pokemon) {
        const ag = this;
        return this.script(() => {
            const berry = pokemon?.heldItem;
            if (
                !pokemon ||
                pokemon.stats.hp <= 0 ||
                !berry ||
                (berry.berryConsumeHpPercent === null || berry.berryConsumeHpPercent === undefined)
            ) {
                return;
            }
            const hpPercent = (pokemon.stats.hp / pokemon.stats.maxHp) * 100;
            if (hpPercent >= berry.berryConsumeHpPercent) return;

            const berryName = berry.name;
            pokemon.heldItem = null;

            if (berry.healAmount !== null && berry.healAmount !== undefined && berry.healAmount > 0) {
                ag.pushHeldBerryConsumptionActions(pokemon, berryName, berry.healAmount);
            } else if (berry.id && pinchBerryStat[berry.id]) {
                ag.pushHeldBerryStatBoostActions(pokemon, berryName, pinchBerryStat[berry.id]);
            }
        });
    }

    roll(sprite, degrees, diameter, angularSpeed = 1) {
        let initialRotation = 0;
        let previousRotation = 0;

        return new BattleAction({
            type: ActionType.ANIMATION,
            description: 'Roll sprite',
            startAction: () => {
                initialRotation = sprite.rotation;
                previousRotation = sprite.rotation;
            },
            animationFunction: () => {
                // Rotate the sprite
                sprite.rotate(angularSpeed);

                // Calculate rotation delta in degrees
                const currentRotation = sprite.rotation;
                const rotationDelta = currentRotation - previousRotation;

                // Convert rotation delta to radians, then calculate horizontal movement
                // For a circle rolling: distance = rotation_in_radians * radius
                // radius = diameter / 2
                const rotationDeltaRadians = (rotationDelta * Math.PI) / 180;
                const horizontalMovement = rotationDeltaRadians * (diameter / 2);

                // Update sprite's x position
                sprite.position.x += horizontalMovement;

                // Update previous rotation for next frame
                previousRotation = currentRotation;

                // Check if we've reached the target rotation
                const totalRotation = currentRotation - initialRotation;
                return Math.abs(totalRotation) >= Math.abs(degrees);
            },
        });
    }

    darken(sprite, amount) {
        return new BattleAction({
            type: ActionType.ANIMATION,
            description: 'Darken sprite',
            animationFunction: () => {
                console.log('darkening sprite', sprite.darkness, amount);
                sprite.darken(amount);
                return sprite.brightness === amount;
            },
        });
    }

    animatePokeballShakes(pokeball, numShakes) {
        pokeball.debugProperty = true;
        const actions = [];
        if (numShakes === 0) {
            return actions;
        }
        if (numShakes >= 1) {
            actions.push(this.roll(pokeball, 30, 16, 3));
            actions.push(this.roll(pokeball, -30, 16, -3));
            actions.push(this.delay(60));
        }
        if (numShakes >= 2) {
            actions.push(this.roll(pokeball, -30, 16, -3));
            actions.push(this.roll(pokeball, 30, 16, 3));
            actions.push(this.delay(60));
        }
        if (numShakes >= 3) {
            actions.push(this.roll(pokeball, 30, 16, 3));
            actions.push(this.roll(pokeball, -30, 16, -3));
            actions.push(this.delay(60));
        }
        if (numShakes >= 4) {
            actions.push(this.darken(pokeball, 0.5));
            actions.push(this.delay(60));
        }
        return actions;
    }
}
